import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, Users, Eye, Camera, Mail, LogOut, Upload, Image as ImageIcon, Tag, Save, X, Edit2, ShoppingBag, Plus, Trash2, Package, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [images, setImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [uploadingServiceImage, setUploadingServiceImage] = useState(false);
  const [uploadingDetailImage, setUploadingDetailImage] = useState(false);
  const serviceFileInputRef = useRef(null);
  const detailImageFileInputRef = useRef(null);
  const promotionFileInputRef = useRef(null);
  const [uploadingPromoImage, setUploadingPromoImage] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingBusiness, setEditingBusiness] = useState(false);
  const [promotion, setPromotion] = useState({
    discount: '20% OFF',
    title: 'Back to School Sale!!',
    description: 'Join the sale event now and get a pair for a discount (or two) before they are all gone.',
    end_date: 'Monday September 30, 2024',
    image: '',
    use_custom_image: false
  });
  const [savingPromotion, setSavingPromotion] = useState(false);
  const [promotionSaved, setPromotionSaved] = useState(false);
  const [savingService, setSavingService] = useState(false);
  const [serviceSaved, setServiceSaved] = useState(false);
  
  // Products state
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    category: 'eyeglasses',
    brand: '',
    sku: '',
    stock: 0,
    images: [''],
    is_active: true,
    is_featured: false,
    has_virtual_tryon: true
  });
  const [savingProduct, setSavingProduct] = useState(false);
  const [uploadingProductImage, setUploadingProductImage] = useState(false);
  const productFileInputRef = useRef(null);
  
  // Site Images state (Hero & Shop)
  const [heroImages, setHeroImages] = useState([]);
  const [shopImages, setShopImages] = useState({ eyeglasses_image: '', sunglasses_image: '' });
  const [savingHeroImages, setSavingHeroImages] = useState(false);
  const [savingShopImages, setSavingShopImages] = useState(false);
  const heroFileInputRef = useRef(null);
  const shopEyeglassesFileInputRef = useRef(null);
  const shopSunglassesFileInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
  });

  const fetchData = async () => {
    try {
      console.log('Fetching data from API...');
      console.log('Auth header:', getAuthHeader());
      
      const [statsRes, contactRes, newsletterRes, imagesRes, businessRes, servicesRes, promotionRes, productsRes] = await Promise.all([
        axios.get(`${API}/analytics/stats`, getAuthHeader()).catch(e => {
          console.error('Stats error:', e.response?.status, e.response?.data);
          return { data: { total_visits: 0, virtual_tryon_uses: 0, photo_captures: 0, form_submissions: 0 }};
        }),
        axios.get(`${API}/contact/submissions`, getAuthHeader()).catch(e => {
          console.error('Contact error:', e.response?.status);
          return { data: [] };
        }),
        axios.get(`${API}/newsletter/subscribers`, getAuthHeader()).catch(e => {
          console.error('Newsletter error:', e.response?.status);
          return { data: [] };
        }),
        axios.get(`${API}/images`, getAuthHeader()).catch(e => {
          console.error('Images error:', e.response?.status, e.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/business-info`).catch(e => {
          console.error('Business error:', e.response?.status);
          return { data: null };
        }),
        axios.get(`${API}/services`).catch(e => {
          console.error('Services error:', e.response?.status);
          return { data: [] };
        }),
        axios.get(`${API}/promotion`).catch(e => {
          console.error('Promotion error:', e.response?.status);
          return { data: null };
        }),
        axios.get(`${API}/products`).catch(e => {
          console.error('Products error:', e.response?.status);
          return { data: [] };
        })
      ]);

      console.log('Images received:', imagesRes.data);
      console.log('Services received:', servicesRes.data);
      
      setStats(statsRes.data);
      setContactSubmissions(contactRes.data);
      setNewsletterSubscribers(newsletterRes.data);
      setImages(imagesRes.data);
      setBusinessInfo(businessRes.data);
      setServices(servicesRes.data);
      setProducts(productsRes.data);
      if (promotionRes.data) {
        setPromotion(promotionRes.data);
      }
      
      // Fetch site settings (hero images and shop images)
      try {
        const heroRes = await axios.get(`${API}/site-settings/hero`);
        if (heroRes.data.images) {
          setHeroImages(heroRes.data.images);
        }
        const shopRes = await axios.get(`${API}/site-settings/shop`);
        if (shopRes.data) {
          setShopImages(shopRes.data);
        }
      } catch (e) {
        console.error('Site settings error:', e);
      }
      
      console.log('Images state set to:', imagesRes.data.length, 'images');
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const handleImageUpload = async (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    formData.append('title', file.name);
    formData.append('alt_text', file.name);

    try {
      await axios.post(`${API}/images/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchData();
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/images/${imageId}`, getAuthHeader());
      fetchData();
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSavePromotion = async () => {
    setSavingPromotion(true);
    setPromotionSaved(false);
    try {
      await axios.put(`${API}/promotion`, promotion, getAuthHeader());
      setPromotionSaved(true);
      setTimeout(() => setPromotionSaved(false), 3000);
    } catch (error) {
      console.error('Error saving promotion:', error);
      alert('Failed to save promotion. Please try again.');
    } finally {
      setSavingPromotion(false);
    }
  };

  const handleSaveService = async () => {
    if (!editingService) return;
    
    setSavingService(true);
    setServiceSaved(false);
    try {
      await axios.put(`${API}/services/${editingService.id}`, {
        title: editingService.title,
        description: editingService.description,
        image: editingService.image,
        detail_image: editingService.detail_image,
        features: editingService.features || []
      }, getAuthHeader());
      
      // Update local services state
      setServices(services.map(s => 
        s.id === editingService.id ? editingService : s
      ));
      
      setServiceSaved(true);
      setTimeout(() => {
        setServiceSaved(false);
        setEditingService(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service. Please try again.');
    } finally {
      setSavingService(false);
    }
  };

  // File upload handler for services
  const handleServiceImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 20 * 1024 * 1024) {
      alert('Image size must be less than 20MB');
      return;
    }

    setUploadingServiceImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Update the editing service with the new image URL
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setEditingService({...editingService, image: imageUrl});
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingServiceImage(false);
      // Reset file input
      if (serviceFileInputRef.current) {
        serviceFileInputRef.current.value = '';
      }
    }
  };

  // File upload handler for detail page hero images
  const handleDetailImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert('Image size must be less than 20MB');
      return;
    }

    setUploadingDetailImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setEditingService({...editingService, detail_image: imageUrl});
      }
    } catch (error) {
      console.error('Error uploading detail image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingDetailImage(false);
      if (detailImageFileInputRef.current) {
        detailImageFileInputRef.current.value = '';
      }
    }
  };

  // File upload handler for promotions
  const handlePromoImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert('Image size must be less than 20MB');
      return;
    }

    setUploadingPromoImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        setPromotion({...promotion, image: imageUrl});
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingPromoImage(false);
      if (promotionFileInputRef.current) {
        promotionFileInputRef.current.value = '';
      }
    }
  };

  // Product image upload handler
  const handleProductImageUpload = async (event, isNewProduct = true) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert('Image size must be less than 20MB');
      return;
    }

    setUploadingProductImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        if (isNewProduct) {
          setNewProduct({...newProduct, images: [imageUrl]});
        } else if (editingProduct) {
          setEditingProduct({...editingProduct, images: [imageUrl]});
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingProductImage(false);
      if (productFileInputRef.current) {
        productFileInputRef.current.value = '';
      }
    }
  };

  // Product management functions
  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      alert('Please fill in product name and price');
      return;
    }
    
    setSavingProduct(true);
    try {
      const productData = {
        ...newProduct,
        price: parseFloat(newProduct.price),
        sale_price: newProduct.sale_price ? parseFloat(newProduct.sale_price) : null,
        images: newProduct.images.filter(img => img.trim() !== '')
      };
      
      const response = await axios.post(`${API}/products`, productData, getAuthHeader());
      setProducts([response.data, ...products]);
      setShowAddProduct(false);
      setNewProduct({
        name: '',
        description: '',
        price: '',
        sale_price: '',
        category: 'eyeglasses',
        brand: '',
        sku: '',
        stock: 0,
        images: [''],
        is_active: true,
        is_featured: false,
        has_virtual_tryon: true
      });
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Failed to add product. Please try again.');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editingProduct) return;
    
    setSavingProduct(true);
    try {
      const productData = {
        ...editingProduct,
        price: parseFloat(editingProduct.price),
        sale_price: editingProduct.sale_price ? parseFloat(editingProduct.sale_price) : null,
        images: editingProduct.images?.filter(img => img.trim() !== '') || []
      };
      
      await axios.put(`${API}/products/${editingProduct.id}`, productData, getAuthHeader());
      setProducts(products.map(p => p.id === editingProduct.id ? {...p, ...productData} : p));
      setEditingProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Failed to update product. Please try again.');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, getAuthHeader());
      setProducts(products.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://lirp.cdn-website.com/1d57ed00/dms3rep/multi/opt/Streetsville+Eyecare+Logo+Full-1920w.png"
                alt="Logo"
                className="h-12"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Streetsville Eyecare</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-700 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'images'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Images ({images.length})
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'content'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Edit Content
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'submissions'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Submissions
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'products'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <ShoppingBag className="w-4 h-4" />
              Products ({products.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Eye className="w-5 h-5 text-blue-600" />
                    Total Visits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats?.total_visits || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Last 30 days</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="w-5 h-5 text-purple-600" />
                    Virtual Try-On
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats?.virtual_tryon_uses || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Uses</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-green-600" />
                    Form Submissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{stats?.form_submissions || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Total</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Mail className="w-5 h-5 text-orange-600" />
                    Newsletter
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold">{newsletterSubscribers.length}</p>
                  <p className="text-sm text-gray-600 mt-1">Subscribers</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Submissions */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Contact Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contactSubmissions.slice(0, 5).map((submission) => (
                    <div key={submission.id} className="border-b border-gray-200 pb-4 last:border-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">{submission.name}</p>
                          <p className="text-sm text-gray-600">{submission.email}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            Services: {submission.services.join(', ')}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(submission.submitted_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                  {contactSubmissions.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No submissions yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'images' && (
          <div className="space-y-8">
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle>Upload New Images</CardTitle>
                <CardDescription>Upload images for different sections of your website</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4">
                  {['hero', 'services', 'gallery', 'collection'].map((category) => (
                    <div key={category} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, category)}
                        className="hidden"
                        id={`upload-${category}`}
                        disabled={uploadingImage}
                      />
                      <label
                        htmlFor={`upload-${category}`}
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="w-12 h-12 text-gray-400 mb-2" />
                        <p className="font-medium text-gray-900">
                          {category === 'collection' ? 'New Arrivals' : category === 'gallery' ? 'Designer Brands' : category.charAt(0).toUpperCase() + category.slice(1)}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Click to upload</p>
                      </label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Image Gallery */}
            <div className="space-y-6">
              {['hero', 'services', 'gallery', 'collection'].map((category) => (
                <Card key={category}>
                  <CardHeader>
                    <CardTitle>
                      {category === 'collection' ? 'New Arrivals Images' : category === 'gallery' ? 'Designer Brands Images' : `${category.charAt(0).toUpperCase() + category.slice(1)} Images`}
                    </CardTitle>
                    <CardDescription>
                      {category === 'collection' ? 'Shows in "New Arrivals" section on homepage' : category === 'gallery' ? 'Shows in "Designer Brands from Italy, Paris..." section' : `Images for ${category} section`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {images
                        .filter((img) => img.category === category)
                        .map((image) => (
                          <div key={image.id} className="relative group">
                            <img
                              src={image.url.startsWith('/api') ? `${BACKEND_URL}${image.url}` : image.url}
                              alt={image.alt_text}
                              className="w-full h-40 object-cover rounded-lg"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                              }}
                            />
                            <button
                              onClick={() => deleteImage(image.id)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center font-bold hover:bg-red-700 shadow-md"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      {images.filter((img) => img.category === category).length === 0 && (
                        <p className="text-gray-500 text-sm col-span-4">No images uploaded yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Service Images */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-green-600" />
                  Service Page Images
                </CardTitle>
                <CardDescription>Images used on service pages (edit these in Edit Content → Services)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {services.map((service) => (
                    <div key={service.id} className="space-y-2">
                      <div className="relative">
                        <img
                          src={service.image?.startsWith('/api') ? `${BACKEND_URL}${service.image}` : service.image}
                          alt={service.title}
                          className="w-full h-32 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                          }}
                        />
                        <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          {service.title}
                        </div>
                      </div>
                      {service.detail_image && (
                        <div className="relative">
                          <img
                            src={service.detail_image?.startsWith('/api') ? `${BACKEND_URL}${service.detail_image}` : service.detail_image}
                            alt={`${service.title} Detail`}
                            className="w-full h-24 object-cover rounded-lg border border-blue-200"
                          />
                          <div className="absolute bottom-1 left-1 bg-blue-600/80 text-white text-xs px-2 py-1 rounded">
                            Detail Hero
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-4">To change these images, go to Edit Content → Edit Service Pages</p>
              </CardContent>
            </Card>

            {/* Hero Slideshow Images */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-blue-600" />
                  Homepage Hero Slideshow
                </CardTitle>
                <CardDescription>Manage the rotating images on your homepage hero section (currently {heroImages.length} images)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {heroImages.map((imageUrl, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageUrl}
                          alt={`Hero slide ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                          }}
                        />
                        <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Slide {index + 1}
                        </div>
                        <button
                          onClick={() => {
                            const newImages = heroImages.filter((_, i) => i !== index);
                            setHeroImages(newImages);
                          }}
                          className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {/* Edit URL */}
                        <input
                          type="text"
                          value={imageUrl}
                          onChange={(e) => {
                            const newImages = [...heroImages];
                            newImages[index] = e.target.value;
                            setHeroImages(newImages);
                          }}
                          className="w-full mt-2 text-xs p-2 border rounded"
                          placeholder="Image URL"
                        />
                      </div>
                    ))}
                    {/* Add new slide */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center min-h-[160px]">
                      <input
                        type="file"
                        accept="image/*"
                        ref={heroFileInputRef}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          try {
                            const response = await axios.post(`${API}/upload`, formData, {
                              ...getAuthHeader(),
                              headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
                            });
                            if (response.data.success) {
                              const imageUrl = `${BACKEND_URL}${response.data.url}`;
                              setHeroImages([...heroImages, imageUrl]);
                            }
                          } catch (error) {
                            console.error('Error uploading:', error);
                            alert('Failed to upload image');
                          }
                          heroFileInputRef.current.value = '';
                        }}
                        className="hidden"
                        id="hero-upload"
                      />
                      <label htmlFor="hero-upload" className="cursor-pointer text-center">
                        <Plus className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">Add Slide</p>
                      </label>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      setSavingHeroImages(true);
                      try {
                        await axios.put(`${API}/site-settings/hero`, heroImages, getAuthHeader());
                        alert('Hero images saved successfully!');
                      } catch (error) {
                        console.error('Error saving hero images:', error);
                        alert('Failed to save hero images');
                      } finally {
                        setSavingHeroImages(false);
                      }
                    }}
                    disabled={savingHeroImages}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    {savingHeroImages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Hero Slideshow
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Shop Section Images */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-green-600" />
                  Shop Online Section Images
                </CardTitle>
                <CardDescription>Manage the "Shop Eyeglasses" and "Shop Sunglasses" images</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Eyeglasses Image */}
                  <div className="space-y-3">
                    <label className="block font-medium text-gray-700">Eyeglasses Image</label>
                    {shopImages.eyeglasses_image && (
                      <img
                        src={shopImages.eyeglasses_image}
                        alt="Shop Eyeglasses"
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                    )}
                    <input
                      type="text"
                      value={shopImages.eyeglasses_image || ''}
                      onChange={(e) => setShopImages({...shopImages, eyeglasses_image: e.target.value})}
                      className="w-full p-2 border rounded text-sm"
                      placeholder="Eyeglasses image URL"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      ref={shopEyeglassesFileInputRef}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const response = await axios.post(`${API}/upload`, formData, {
                            ...getAuthHeader(),
                            headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
                          });
                          if (response.data.success) {
                            const imageUrl = `${BACKEND_URL}${response.data.url}`;
                            setShopImages({...shopImages, eyeglasses_image: imageUrl});
                          }
                        } catch (error) {
                          console.error('Error uploading:', error);
                        }
                        shopEyeglassesFileInputRef.current.value = '';
                      }}
                      className="hidden"
                      id="shop-eyeglasses-upload"
                    />
                    <label htmlFor="shop-eyeglasses-upload" className="block w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded cursor-pointer">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload from Desktop
                    </label>
                  </div>

                  {/* Sunglasses Image */}
                  <div className="space-y-3">
                    <label className="block font-medium text-gray-700">Sunglasses Image</label>
                    {shopImages.sunglasses_image && (
                      <img
                        src={shopImages.sunglasses_image}
                        alt="Shop Sunglasses"
                        className="w-full h-48 object-cover rounded-lg border"
                      />
                    )}
                    <input
                      type="text"
                      value={shopImages.sunglasses_image || ''}
                      onChange={(e) => setShopImages({...shopImages, sunglasses_image: e.target.value})}
                      className="w-full p-2 border rounded text-sm"
                      placeholder="Sunglasses image URL"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      ref={shopSunglassesFileInputRef}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const response = await axios.post(`${API}/upload`, formData, {
                            ...getAuthHeader(),
                            headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
                          });
                          if (response.data.success) {
                            const imageUrl = `${BACKEND_URL}${response.data.url}`;
                            setShopImages({...shopImages, sunglasses_image: imageUrl});
                          }
                        } catch (error) {
                          console.error('Error uploading:', error);
                        }
                        shopSunglassesFileInputRef.current.value = '';
                      }}
                      className="hidden"
                      id="shop-sunglasses-upload"
                    />
                    <label htmlFor="shop-sunglasses-upload" className="block w-full text-center bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded cursor-pointer">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload from Desktop
                    </label>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    setSavingShopImages(true);
                    try {
                      await axios.put(`${API}/site-settings/shop?eyeglasses_image=${encodeURIComponent(shopImages.eyeglasses_image)}&sunglasses_image=${encodeURIComponent(shopImages.sunglasses_image)}`, {}, getAuthHeader());
                      alert('Shop images saved successfully!');
                    } catch (error) {
                      console.error('Error saving shop images:', error);
                      alert('Failed to save shop images');
                    } finally {
                      setSavingShopImages(false);
                    }
                  }}
                  disabled={savingShopImages}
                  className="w-full mt-4 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  {savingShopImages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Shop Images
                </button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'content' && (
          <div className="space-y-6">
            {/* Monthly Specials / Promotion Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5 text-red-600" />
                  Monthly Specials / Promotion Banner
                </CardTitle>
                <CardDescription>Upload your monthly promotional image or use text-based promotion</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Toggle between custom image and text */}
                  <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                    <label className="flex items-center gap-3 cursor-pointer flex-1 p-4 rounded-lg border-2 transition-all hover:border-red-300"
                      style={{ borderColor: !promotion.use_custom_image ? '#dc2626' : '#e5e7eb', backgroundColor: !promotion.use_custom_image ? '#fef2f2' : 'white' }}>
                      <input
                        type="radio"
                        name="promo_type"
                        checked={!promotion.use_custom_image}
                        onChange={() => setPromotion({...promotion, use_custom_image: false})}
                        className="w-5 h-5 text-red-600"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Text-Based Promotion</p>
                        <p className="text-sm text-gray-500">Enter discount, title, and description</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer flex-1 p-4 rounded-lg border-2 transition-all hover:border-red-300"
                      style={{ borderColor: promotion.use_custom_image ? '#dc2626' : '#e5e7eb', backgroundColor: promotion.use_custom_image ? '#fef2f2' : 'white' }}>
                      <input
                        type="radio"
                        name="promo_type"
                        checked={promotion.use_custom_image}
                        onChange={() => setPromotion({...promotion, use_custom_image: true})}
                        className="w-5 h-5 text-red-600"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Custom Image</p>
                        <p className="text-sm text-gray-500">Upload your own promotional graphic</p>
                      </div>
                    </label>
                  </div>

                  {/* Custom Image Upload Section */}
                  {promotion.use_custom_image ? (
                    <div className="space-y-4 p-6 border-2 border-dashed border-gray-300 rounded-lg">
                      <div className="text-center mb-4">
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <h3 className="font-medium text-gray-900">Upload Your Promotional Image</h3>
                        <p className="text-sm text-gray-500">Upload from your computer or paste an image URL (recommended size: 1200x400 pixels)</p>
                      </div>
                      
                      {/* Upload from Desktop Button */}
                      <div className="flex items-center justify-center gap-4 py-4 bg-white rounded-lg">
                        <input
                          type="file"
                          ref={promotionFileInputRef}
                          accept="image/*"
                          onChange={handlePromoImageUpload}
                          className="hidden"
                          id="promo-image-upload"
                        />
                        <label
                          htmlFor="promo-image-upload"
                          className={`flex items-center gap-2 px-8 py-3 rounded-lg cursor-pointer transition-colors font-medium ${
                            uploadingPromoImage 
                              ? 'bg-gray-400 cursor-not-allowed text-white' 
                              : 'bg-red-600 hover:bg-red-700 text-white'
                          }`}
                        >
                          {uploadingPromoImage ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="w-5 h-5" />
                              Upload from Desktop
                            </>
                          )}
                        </label>
                        <span className="text-gray-400 font-medium">or</span>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Paste Image URL</label>
                        <input 
                          type="text" 
                          value={promotion.image || ''}
                          onChange={(e) => setPromotion({...promotion, image: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                          placeholder="https://example.com/your-promo-banner.jpg"
                        />
                      </div>

                      {/* Image Preview */}
                      {promotion.image ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Preview (how it will appear on your website)</label>
                          <div className="bg-gray-100 p-4 rounded-lg">
                            <img 
                              src={promotion.image} 
                              alt="Your promotion" 
                              className="w-full h-auto rounded-lg shadow-lg"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/1200x400?text=Invalid+Image+URL';
                              }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-100 p-8 rounded-lg text-center">
                          <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-2" />
                          <p className="text-gray-500">Paste an image URL above to see preview</p>
                        </div>
                      )}

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <strong>Tips:</strong> Create your promotional image using Canva, Photoshop, or any design tool. 
                          Upload it to a free image host like <a href="https://imgur.com" target="_blank" rel="noopener noreferrer" className="underline">Imgur</a> or 
                          <a href="https://imgbb.com" target="_blank" rel="noopener noreferrer" className="underline"> ImgBB</a>, then paste the direct image URL here.
                        </p>
                      </div>
                    </div>
                  ) : (
                    /* Text-Based Promotion Fields */
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Discount Text</label>
                          <input 
                            type="text" 
                            value={promotion.discount}
                            onChange={(e) => setPromotion({...promotion, discount: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                            placeholder="e.g., 20% OFF, BOGO, $50 OFF"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                          <input 
                            type="text" 
                            value={promotion.title}
                            onChange={(e) => setPromotion({...promotion, title: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                            placeholder="e.g., Back to School Sale!!"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea 
                          value={promotion.description}
                          onChange={(e) => setPromotion({...promotion, description: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                          rows={2}
                          placeholder="Describe your promotion..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sale End Date</label>
                        <input 
                          type="text" 
                          value={promotion.end_date}
                          onChange={(e) => setPromotion({...promotion, end_date: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                          placeholder="e.g., Monday September 30, 2024"
                        />
                      </div>
                      
                      {/* Text-based Preview */}
                      <div className="bg-gradient-to-br from-red-600 to-red-700 text-white p-6 rounded-lg">
                        <p className="text-xs text-white/70 mb-2">PREVIEW:</p>
                        <div className="text-center">
                          <div className="inline-block bg-white text-red-600 px-4 py-2 rounded-full text-xl font-bold mb-2">
                            {promotion.discount || '20% OFF'}
                          </div>
                          <h3 className="text-lg font-bold">{promotion.title || 'Your Sale Title'}</h3>
                          <p className="text-sm opacity-90">{promotion.description || 'Your description here'}</p>
                          <p className="text-xs mt-1 opacity-75">Sale ends {promotion.end_date || 'Date'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleSavePromotion}
                    disabled={savingPromotion}
                    className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-lg"
                  >
                    <Save className="w-5 h-5" />
                    {savingPromotion ? 'Saving...' : promotionSaved ? '✓ Saved!' : 'Save Promotion'}
                  </button>

                  {promotionSaved && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800 text-center">
                        ✅ Promotion updated! Changes are now live on your website.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* New Arrivals/Collection Section */}
            <Card>
              <CardHeader>
                <CardTitle>New Arrivals / New Collection</CardTitle>
                <CardDescription>Manage your "Check out our New Arrivals" section - each image with its description</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                    <input 
                      type="text" 
                      defaultValue="Come check out our New Arrivals"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                      placeholder="Section heading..."
                    />
                  </div>
                  
                  {/* Image + Description pairs */}
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-gray-700">Collection Items (Image + Description):</p>
                    
                    {images.filter(img => img.category === 'collection').map((image, index) => (
                      <div key={image.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex gap-4">
                          <div className="relative w-32 h-24 flex-shrink-0">
                            <img
                              src={image.url.startsWith('/api') ? `${BACKEND_URL}${image.url}` : image.url}
                              alt={image.title}
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <button
                              onClick={() => deleteImage(image.id)}
                              className="absolute -top-2 -right-2 bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold hover:bg-red-700 shadow-md"
                            >
                              ×
                            </button>
                            {index < 3 && (
                              <span className="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-1 rounded">Visible</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">Description for this image:</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={image.alt_text || ''}
                                onChange={(e) => {
                                  // Update local state
                                  const newImages = images.map(img => 
                                    img.id === image.id ? {...img, alt_text: e.target.value} : img
                                  );
                                  setImages(newImages);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Enter description for this item..."
                              />
                              <button 
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/images/${image.id}`, {
                                      alt_text: image.alt_text
                                    }, getAuthHeader());
                                    alert('Description saved!');
                                  } catch (error) {
                                    console.error('Error saving:', error);
                                    alert('Error saving description');
                                  }
                                }}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Upload new image */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">Add New Item</p>
                      <p className="text-sm text-gray-600 mb-3">Upload image, then add description</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'collection')}
                        className="hidden"
                        id="collection-upload"
                      />
                      <label
                        htmlFor="collection-upload"
                        className="inline-block bg-red-600 text-white px-6 py-2 rounded-md cursor-pointer hover:bg-red-700"
                      >
                        Choose Image
                      </label>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-800">
                      ✅ <strong>Tip:</strong> Each image shows with its description below it on the website. First 3 items are visible.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Designer Brands Section */}
            <Card>
              <CardHeader>
                <CardTitle>Designer Brands Gallery</CardTitle>
                <CardDescription>Manage your "Designer Brands From Italy, Paris, England, Canada & More!" section</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center mb-4">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">Upload Designer Brand Images</p>
                      <p className="text-sm text-gray-600">Add photos of your supplier brands (shows 3 images on website)</p>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files);
                          files.forEach(file => handleImageUpload({ target: { files: [file] }}, 'gallery'));
                        }}
                        className="hidden"
                        id="gallery-upload-edit"
                      />
                      <label
                        htmlFor="gallery-upload-edit"
                        className="mt-4 inline-block bg-red-600 text-white px-6 py-2 rounded-md cursor-pointer hover:bg-red-700"
                      >
                        Choose Images
                      </label>
                    </div>
                    
                    {/* Show uploaded gallery images */}
                    {images.filter(img => img.category === 'gallery').length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium text-gray-700 mb-2">Designer Brands Images (first 3 show on website):</p>
                        <div className="grid grid-cols-3 gap-3">
                          {images.filter(img => img.category === 'gallery').map((image, index) => (
                            <div key={image.id} className="relative">
                              <img
                                src={image.url.startsWith('/api') ? `${BACKEND_URL}${image.url}` : image.url}
                                alt={image.title}
                                className={`w-full h-24 object-cover rounded-lg border-2 ${index < 3 ? 'border-green-500' : 'border-gray-200'}`}
                              />
                              <button
                                onClick={() => deleteImage(image.id)}
                                className="absolute -top-2 -right-2 bg-red-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold hover:bg-red-700 shadow-md"
                              >
                                ×
                              </button>
                              {index < 3 && (
                                <span className="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-1 rounded">Visible</span>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">These show in "Designer Brands" section on homepage</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Services Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Edit2 className="w-5 h-5 text-red-600" />
                  Edit Service Pages
                </CardTitle>
                <CardDescription>Change service images and descriptions. These appear on individual service pages.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {services.map((service) => (
                    <div 
                      key={service.id} 
                      className={`border rounded-lg overflow-hidden transition-all ${
                        editingService?.id === service.id 
                          ? 'border-red-600 ring-2 ring-red-100' 
                          : 'border-gray-200 hover:border-red-400'
                      }`}
                    >
                      {editingService?.id === service.id ? (
                        // Editing mode
                        <div className="p-6 space-y-4 bg-gray-50">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-lg text-gray-900">Editing: {service.title}</h3>
                            <button 
                              onClick={() => setEditingService(null)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                          
                          {/* Image Upload Section */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Service Image
                            </label>
                            
                            {/* Upload from Desktop */}
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-3 bg-white">
                              <div className="flex items-center justify-center gap-4">
                                <input
                                  type="file"
                                  ref={serviceFileInputRef}
                                  accept="image/*"
                                  onChange={handleServiceImageUpload}
                                  className="hidden"
                                  id={`service-image-upload-${service.id}`}
                                />
                                <label
                                  htmlFor={`service-image-upload-${service.id}`}
                                  className={`flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition-colors ${
                                    uploadingServiceImage 
                                      ? 'bg-gray-400 cursor-not-allowed' 
                                      : 'bg-red-600 hover:bg-red-700 text-white'
                                  }`}
                                >
                                  {uploadingServiceImage ? (
                                    <>
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-5 h-5" />
                                      Upload from Desktop
                                    </>
                                  )}
                                </label>
                                <span className="text-gray-400">or</span>
                              </div>
                            </div>

                            {/* Paste URL Option */}
                            <div className="flex gap-2">
                              <input 
                                type="text" 
                                value={editingService.image || ''}
                                onChange={(e) => setEditingService({...editingService, image: e.target.value})}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                                placeholder="Or paste image URL here..."
                              />
                            </div>
                          </div>
                          
                          {/* Image Preview */}
                          {editingService.image && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">Thumbnail Preview</label>
                              <img 
                                src={editingService.image?.startsWith('/api') ? `${BACKEND_URL}${editingService.image}` : editingService.image} 
                                alt="Preview" 
                                className="w-full h-48 object-cover rounded-lg border"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/800x400?text=Invalid+Image+URL';
                                }}
                              />
                            </div>
                          )}

                          {/* Detail Page Hero Image Section */}
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Detail Page Hero Image
                              <span className="ml-2 text-xs text-gray-500 font-normal">(Large image shown on individual service page)</span>
                            </label>
                            
                            {/* Upload from Desktop for Detail Image */}
                            <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 mb-3 bg-blue-50">
                              <div className="flex items-center justify-center gap-4">
                                <input
                                  type="file"
                                  ref={detailImageFileInputRef}
                                  accept="image/*"
                                  onChange={handleDetailImageUpload}
                                  className="hidden"
                                  id={`detail-image-upload-${service.id}`}
                                />
                                <label
                                  htmlFor={`detail-image-upload-${service.id}`}
                                  className={`flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition-colors ${
                                    uploadingDetailImage 
                                      ? 'bg-gray-400 cursor-not-allowed' 
                                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                                  }`}
                                >
                                  {uploadingDetailImage ? (
                                    <>
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                      Uploading...
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-5 h-5" />
                                      Upload Hero Image
                                    </>
                                  )}
                                </label>
                                <span className="text-gray-400">or</span>
                              </div>
                            </div>

                            {/* Paste URL Option for Detail Image */}
                            <div className="flex gap-2">
                              <input 
                                type="text" 
                                value={editingService.detail_image || ''}
                                onChange={(e) => setEditingService({...editingService, detail_image: e.target.value})}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                                placeholder="Or paste hero image URL here..."
                              />
                            </div>
                            
                            {/* Detail Image Preview */}
                            {editingService.detail_image && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Hero Image Preview</label>
                                <img 
                                  src={editingService.detail_image?.startsWith('/api') ? `${BACKEND_URL}${editingService.detail_image}` : editingService.detail_image} 
                                  alt="Detail Page Preview" 
                                  className="w-full h-48 object-cover object-top rounded-lg border"
                                  onError={(e) => {
                                    e.target.src = 'https://via.placeholder.com/800x400?text=Invalid+Image+URL';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                          
                          {/* Description */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <textarea 
                              value={editingService.description || ''}
                              onChange={(e) => setEditingService({...editingService, description: e.target.value})}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                              rows={3}
                              placeholder="Service description..."
                            />
                          </div>

                          {/* What We Offer - Features */}
                          <div className="mt-6 pt-6 border-t border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              What We Offer
                              <span className="ml-2 text-xs text-gray-500 font-normal">(Bullet points shown on service detail page)</span>
                            </label>
                            <div className="space-y-2">
                              {(editingService.features || []).map((feature, index) => (
                                <div key={index} className="flex gap-2">
                                  <span className="text-red-600 mt-2">✓</span>
                                  <input
                                    type="text"
                                    value={feature}
                                    onChange={(e) => {
                                      const newFeatures = [...(editingService.features || [])];
                                      newFeatures[index] = e.target.value;
                                      setEditingService({...editingService, features: newFeatures});
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                                    placeholder="Enter feature..."
                                  />
                                  <button
                                    onClick={() => {
                                      const newFeatures = (editingService.features || []).filter((_, i) => i !== index);
                                      setEditingService({...editingService, features: newFeatures});
                                    }}
                                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => {
                                  const newFeatures = [...(editingService.features || []), ''];
                                  setEditingService({...editingService, features: newFeatures});
                                }}
                                className="w-full mt-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-600 hover:text-red-600 flex items-center justify-center gap-2 transition-colors"
                              >
                                <Plus className="w-4 h-4" />
                                Add Feature
                              </button>
                            </div>
                          </div>
                          
                          {/* Save Button */}
                          <div className="flex gap-3 pt-4">
                            <button
                              onClick={handleSaveService}
                              disabled={savingService}
                              className="flex-1 flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors font-medium"
                            >
                              <Save className="w-5 h-5" />
                              {savingService ? 'Saving...' : serviceSaved ? '✓ Saved!' : 'Save Changes'}
                            </button>
                            <button
                              onClick={() => setEditingService(null)}
                              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                          
                          {serviceSaved && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-sm text-green-800 text-center">
                                ✅ Service updated! Changes are now live on your website.
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Display mode
                        <div 
                          className="flex items-center gap-4 p-4 cursor-pointer"
                          onClick={() => setEditingService({...service})}
                        >
                          <img 
                            src={service.image?.startsWith('/api') ? `${BACKEND_URL}${service.image}` : service.image} 
                            alt={service.title} 
                            className="w-24 h-24 object-cover rounded-lg"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                            }}
                          />
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900">{service.title}</h3>
                            <p className="text-sm text-gray-600 line-clamp-2">{service.description}</p>
                          </div>
                          <button className="text-red-600 hover:text-red-700 font-medium">
                            Edit →
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Tip:</strong> To find free images, visit <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="underline">Unsplash.com</a> or <a href="https://pexels.com" target="_blank" rel="noopener noreferrer" className="underline">Pexels.com</a>, search for your service type, and copy the image URL.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {businessInfo && (
              <Card>
                <CardHeader>
                  <CardTitle>Business Information</CardTitle>
                  <CardDescription>Update your business contact details and hours</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Business Name</label>
                        <input 
                          type="text" 
                          value={businessInfo.name}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Phone</label>
                        <input 
                          type="text" 
                          value={businessInfo.phone}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Email</label>
                        <input 
                          type="email" 
                          value={businessInfo.email}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Address</label>
                        <input 
                          type="text" 
                          value={`${businessInfo.address.street}, ${businessInfo.address.unit}, ${businessInfo.address.city}, ${businessInfo.address.province} ${businessInfo.address.postalCode}`}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-blue-800">
                        💡 <strong>Note:</strong> To edit business information, contact your developer or use the API directly.
                        Full editing interface coming soon!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'submissions' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Contact Form Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contactSubmissions.map((submission) => (
                    <div key={submission.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="font-medium text-gray-900">{submission.name}</p>
                          <p className="text-sm text-gray-600">{submission.email}</p>
                          <p className="text-sm text-gray-600">{submission.phone}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Preferred: {submission.contact_method}</p>
                          <p className="text-sm text-gray-600">Services: {submission.services.join(', ')}</p>
                        </div>
                      </div>
                      {submission.message && (
                        <p className="text-sm text-gray-700 mt-2 p-3 bg-gray-50 rounded">{submission.message}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(submission.submitted_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Newsletter Subscribers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {newsletterSubscribers.map((subscriber) => (
                    <div key={subscriber.id} className="flex justify-between items-center py-2 border-b border-gray-200">
                      <p className="text-gray-900">{subscriber.email}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(subscriber.subscribed_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            {/* Add Product Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Product Management</h2>
                <p className="text-gray-600">Add, edit, and manage your eyewear collection</p>
              </div>
              <button
                onClick={() => setShowAddProduct(true)}
                className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Add New Product
              </button>
            </div>

            {/* Add Product Form */}
            {showAddProduct && (
              <Card className="border-2 border-red-200">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Add New Product</span>
                    <button onClick={() => setShowAddProduct(false)} className="text-gray-500 hover:text-gray-700">
                      <X className="w-5 h-5" />
                    </button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Name *</label>
                      <input
                        type="text"
                        value={newProduct.name}
                        onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        placeholder="e.g., Classic Round Frames"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                      <input
                        type="text"
                        value={newProduct.brand}
                        onChange={(e) => setNewProduct({...newProduct, brand: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        placeholder="e.g., Ray-Ban"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                      <select
                        value={newProduct.category}
                        onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                      >
                        <option value="eyeglasses">Eyeglasses</option>
                        <option value="sunglasses">Sunglasses</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SKU</label>
                      <input
                        type="text"
                        value={newProduct.sku}
                        onChange={(e) => setNewProduct({...newProduct, sku: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        placeholder="e.g., SKU-001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Price ($) *</label>
                      <input
                        type="number"
                        value={newProduct.price}
                        onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        placeholder="199.99"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sale Price ($)</label>
                      <input
                        type="number"
                        value={newProduct.sale_price}
                        onChange={(e) => setNewProduct({...newProduct, sale_price: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        placeholder="149.99 (optional)"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stock Quantity</label>
                      <input
                        type="number"
                        value={newProduct.stock}
                        onChange={(e) => setNewProduct({...newProduct, stock: parseInt(e.target.value) || 0})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
                      
                      {/* Upload from Desktop */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 mb-3 bg-white">
                        <div className="flex items-center justify-center gap-4">
                          <input
                            type="file"
                            ref={productFileInputRef}
                            accept="image/*"
                            onChange={(e) => handleProductImageUpload(e, true)}
                            className="hidden"
                            id="new-product-image-upload"
                          />
                          <label
                            htmlFor="new-product-image-upload"
                            className={`flex items-center gap-2 px-6 py-3 rounded-lg cursor-pointer transition-colors ${
                              uploadingProductImage 
                                ? 'bg-gray-400 cursor-not-allowed text-white' 
                                : 'bg-red-600 hover:bg-red-700 text-white'
                            }`}
                          >
                            {uploadingProductImage ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-5 h-5" />
                                Upload from Desktop
                              </>
                            )}
                          </label>
                          <span className="text-gray-400">or</span>
                        </div>
                      </div>
                      
                      {/* Paste URL */}
                      <input
                        type="text"
                        value={newProduct.images[0]}
                        onChange={(e) => setNewProduct({...newProduct, images: [e.target.value]})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        placeholder="Or paste image URL here..."
                      />
                      
                      {/* Image Preview */}
                      {newProduct.images[0] && (
                        <div className="mt-3">
                          <img 
                            src={newProduct.images[0]} 
                            alt="Preview" 
                            className="w-32 h-32 object-cover rounded-lg border"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/128x128?text=Invalid';
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        value={newProduct.description}
                        onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600"
                        rows={3}
                        placeholder="Describe the product..."
                      />
                    </div>
                    <div className="md:col-span-2 flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newProduct.is_active}
                          onChange={(e) => setNewProduct({...newProduct, is_active: e.target.checked})}
                          className="w-4 h-4 text-red-600"
                        />
                        <span className="text-sm text-gray-700">Active (visible on website)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newProduct.is_featured}
                          onChange={(e) => setNewProduct({...newProduct, is_featured: e.target.checked})}
                          className="w-4 h-4 text-red-600"
                        />
                        <span className="text-sm text-gray-700">Featured Product</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newProduct.has_virtual_tryon}
                          onChange={(e) => setNewProduct({...newProduct, has_virtual_tryon: e.target.checked})}
                          className="w-4 h-4 text-red-600"
                        />
                        <span className="text-sm text-gray-700">Enable Virtual Try-On</span>
                      </label>
                    </div>
                  </div>
                  <div className="mt-6 flex gap-4">
                    <button
                      onClick={handleAddProduct}
                      disabled={savingProduct}
                      className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <Save className="w-5 h-5" />
                      {savingProduct ? 'Saving...' : 'Save Product'}
                    </button>
                    <button
                      onClick={() => setShowAddProduct(false)}
                      className="px-8 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Products List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-red-600" />
                  All Products
                </CardTitle>
                <CardDescription>
                  {products.length} products in your catalog
                </CardDescription>
              </CardHeader>
              <CardContent>
                {products.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-600">No products yet</h3>
                    <p className="text-gray-500 mt-2">Click "Add New Product" to get started</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {products.map((product) => (
                      <div 
                        key={product.id}
                        className={`border rounded-lg overflow-hidden ${
                          editingProduct?.id === product.id 
                            ? 'border-red-600 ring-2 ring-red-100' 
                            : 'border-gray-200'
                        }`}
                      >
                        {editingProduct?.id === product.id ? (
                          // Edit Mode
                          <div className="p-6 bg-gray-50 space-y-4">
                            <div className="flex justify-between items-center">
                              <h3 className="font-bold text-lg">Editing: {product.name}</h3>
                              <button onClick={() => setEditingProduct(null)} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                  type="text"
                                  value={editingProduct.name}
                                  onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                                <input
                                  type="text"
                                  value={editingProduct.brand || ''}
                                  onChange={(e) => setEditingProduct({...editingProduct, brand: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                                <input
                                  type="number"
                                  value={editingProduct.price}
                                  onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                  step="0.01"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price ($)</label>
                                <input
                                  type="number"
                                  value={editingProduct.sale_price || ''}
                                  onChange={(e) => setEditingProduct({...editingProduct, sale_price: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                  step="0.01"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
                                <input
                                  type="number"
                                  value={editingProduct.stock}
                                  onChange={(e) => setEditingProduct({...editingProduct, stock: parseInt(e.target.value) || 0})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select
                                  value={editingProduct.category}
                                  onChange={(e) => setEditingProduct({...editingProduct, category: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                  <option value="eyeglasses">Eyeglasses</option>
                                  <option value="sunglasses">Sunglasses</option>
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
                                
                                {/* Upload Button */}
                                <div className="flex items-center gap-3 mb-2">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleProductImageUpload(e, false)}
                                    className="hidden"
                                    id={`edit-product-image-${editingProduct.id}`}
                                  />
                                  <label
                                    htmlFor={`edit-product-image-${editingProduct.id}`}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                                      uploadingProductImage 
                                        ? 'bg-gray-400 cursor-not-allowed text-white' 
                                        : 'bg-red-600 hover:bg-red-700 text-white'
                                    }`}
                                  >
                                    {uploadingProductImage ? (
                                      <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="w-4 h-4" />
                                        Upload New Image
                                      </>
                                    )}
                                  </label>
                                  <span className="text-gray-400 text-sm">or paste URL below</span>
                                </div>
                                
                                <input
                                  type="text"
                                  value={editingProduct.images?.[0] || ''}
                                  onChange={(e) => setEditingProduct({...editingProduct, images: [e.target.value]})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                  placeholder="Paste image URL..."
                                />
                                
                                {/* Preview */}
                                {editingProduct.images?.[0] && (
                                  <img 
                                    src={editingProduct.images[0]} 
                                    alt="Preview" 
                                    className="mt-2 w-24 h-24 object-cover rounded-lg border"
                                  />
                                )}
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                  value={editingProduct.description}
                                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                  rows={2}
                                />
                              </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                              <button
                                onClick={handleUpdateProduct}
                                disabled={savingProduct}
                                className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                              >
                                {savingProduct ? 'Saving...' : 'Save Changes'}
                              </button>
                              <button
                                onClick={() => setEditingProduct(null)}
                                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div className="flex items-center gap-4 p-4">
                            <img
                              src={product.images?.[0] || 'https://via.placeholder.com/100x100?text=No+Image'}
                              alt={product.name}
                              className="w-20 h-20 object-cover rounded-lg"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-gray-900">{product.name}</h3>
                                {product.is_featured && (
                                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Featured</span>
                                )}
                                {!product.is_active && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Hidden</span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{product.brand} • {product.category}</p>
                              <div className="flex items-center gap-4 mt-1">
                                <span className="font-medium text-gray-900">
                                  ${product.sale_price || product.price}
                                  {product.sale_price && (
                                    <span className="text-sm text-gray-400 line-through ml-2">${product.price}</span>
                                  )}
                                </span>
                                <span className={`text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setEditingProduct({...product})}
                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                <Edit2 className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product.id)}
                                className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;